/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import toast from "react-hot-toast";

import { toJSON } from "./Fetcher";

interface FetchSuccessOptions<T = any> {
  message: string | null;
  shapeData: ((data: any) => T) | null;
}

class FetchSuccess<T = any> {
  data: T | null;
  ok: boolean;
  status: "SUCCESS" | "INITIAL";
  statusCode: number;
  error: null;
  response: Response | null;

  private message: FetchSuccessOptions["message"];
  private shapeData: FetchSuccessOptions<T>["shapeData"];

  constructor(res: Response, options?: FetchSuccessOptions<T> | string) {
    const settings = {
      message: "",
      shapeData: null,
    };

    if (options) {
      if (typeof options === "string") settings.message = options;
      if (typeof options === "object") Object.assign(settings, options);
    }

    this.message = settings.message;
    this.shapeData = settings.shapeData;

    if (this.message.length > 0) toast.success(this.message);

    this.data = null;
    this.ok = false;
    this.status = "INITIAL";
    this.statusCode = 0;
    this.error = null;
    this.response = res;

    // @ts-ignore
    return Promise.resolve(this.init(res));
  }

  init = async (response: Response) => {
    if (response.status === 204) return this.handle204(response);

    return toJSON(response).then((data) => this.handle200(data, response));
  };

  private handle200 = (data: any, response: Response) => {
    let _data = data;

    // to skip pagination payload.
    if (response && response.url.includes("offset") === false) {
      if (data.hasOwnProperty("data")) _data = data.data;
    }

    if (this.shapeData) _data = this.shapeData(_data);

    this.data = _data;
    this.status = "SUCCESS";
    this.ok = true;
    this.error = null;
    this.statusCode = 200;
    this.response = null;

    return this;
  };

  private handle204 = (response: Response) => {
    this.data = null;
    this.status = "SUCCESS";
    this.ok = true;
    this.error = null;
    this.statusCode = response.status;
    this.response = response;

    return this;
  };
}

export default FetchSuccess;
